import React from "react";
import Loader from "../common/Loader";

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  fullWidth?: boolean;
  size?: "sm" | "md" | "lg";
  color?:
    | "primary"
    | "secondary"
    | "default"
    | "white"
    | "success"
    | "danger"
    | "warning";
  variant?: "contained" | "outlined" | "text";
  rounded?: "sm" | "md" | "lg" | "xl" | "full";
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
}

//
const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  fullWidth = false,
  size = "md",
  color = "primary",
  variant = "contained",
  rounded = "xl",
  disabled = false,
  loading = false,
  type = "button",
}) => {
  const sizeClass = {
    sm: "px-2 py-1 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  };

  const colorClass = {
    primary: "var(--app-primary)",
    secondary: "var(--app-secondary)",
    default: "#000000",
    white: "white",
    success: "#10B981",
    danger: "#DC2626",
    warning: "#F59E0B",
  };

  const textColorClass = {
    primary: "white",
    secondary: "white",
    default: "white",
    white: "black",
    success: "white",
    danger: "white",
    warning: "white",
  };

  const roundedClass = {
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  };

  const disabledClass = "disabled:opacity-50 disabled:cursor-not-allowed";

  // Get the current color value
  const currentColor = colorClass[color];
  const currentTextColor = textColorClass[color];

  // Generate inline styles for dynamic colors
  const getInlineStyles = () => {
    if (variant === "contained") {
      return {
        backgroundColor: currentColor,
        color: currentTextColor,
        borderColor: currentColor,
      };
    } else if (variant === "outlined") {
      return {
        backgroundColor: "white",
        color: currentColor,
        borderColor: currentColor,
      };
    } else if (variant === "text") {
      return {
        backgroundColor: "transparent",
        color: currentColor,
        borderColor: "transparent",
      };
    }
    return {};
  };

  return (
    <button
      className={`flex justify-center items-center whitespace-nowrap ${
        sizeClass[size]
      } ${fullWidth ? "w-full" : ""} border-2 active:opacity-90 ${
        roundedClass[rounded]
      } ${disabled ? disabledClass : ""}`}
      style={getInlineStyles()}
      onClick={onClick}
      disabled={disabled}
      type={type}
    >
      {loading ? (
        <Loader
          size={size}
          color={variant === "contained" ? "info" : "primary"}
        />
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
